import React, { useState, useEffect } from 'react';
import { Employee } from '../../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  ContactPhone as ContactIcon
} from '@mui/icons-material';

// Extended interface for the dialog with additional properties
interface EmployeeExtended extends Employee {
  employee_number?: string;
  date_of_birth?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  work_location?: string;
  employee_type?: string;
  probation_end_date?: string;
  annual_salary?: number;
  hourly_rate?: number;
  payroll_frequency?: string;
  social_security_number?: string;
  student_loan?: boolean;
  bank_name?: string;
  account_holder_name?: string;
  routing_number?: string;
  account_number?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
}

interface EditEmployeeDialogProps {
  open: boolean;
  employee: EmployeeExtended | null;
  onClose: () => void;
  onSave: (employee: EmployeeExtended) => void;
}

const EditEmployeeDialog: React.FC<EditEmployeeDialogProps> = ({
  open,
  employee,
  onClose,
  onSave
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [editData, setEditData] = useState<EmployeeExtended | null>(null);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (employee) {
      setEditData({ ...employee });
    }
  }, [employee]);

  if (!employee || !editData) return null;

  const handleInputChange = (field: string, value: any) => {
    setEditData(prev => prev ? { ...prev, [field]: value } : null);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    if (editData) {
      onSave(editData);
      onClose();
    }
  };

  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // Convert to YYYY-MM-DD format
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Edit Employee: {employee.first_name} {employee.last_name}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
            <Tab label="Personal" />
            <Tab label="Employment" />
            <Tab label="Salary & Benefits" />
            <Tab label="Tax & Banking" />
            <Tab label="Emergency Contact" />
          </Tabs>
        </Box>

        {/* Personal Information Tab */}
        <TabPanel value={selectedTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={editData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formatDateForInput(editData.date_of_birth)}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={editData.address_line_1 || ''}
                onChange={(e) => handleInputChange('address_line_1', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={editData.address_line_2 || ''}
                onChange={(e) => handleInputChange('address_line_2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={editData.city || ''}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="County"
                value={editData.county || ''}
                onChange={(e) => handleInputChange('county', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Postcode"
                value={editData.postcode || ''}
                onChange={(e) => handleInputChange('postcode', e.target.value.toUpperCase())}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Employment Tab */}
        <TabPanel value={selectedTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employee Number"
                value={editData.employee_number}
                onChange={(e) => handleInputChange('employee_number', e.target.value)}
                disabled
                helperText="Employee number cannot be changed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={editData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                >
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="IT">IT</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                  <MenuItem value="Warehouse">Warehouse</MenuItem>
                  <MenuItem value="Cleaning">Cleaning</MenuItem>
                  <MenuItem value="Management">Management</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={editData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Work Location</InputLabel>
                <Select
                  value={editData.work_location}
                  onChange={(e) => handleInputChange('work_location', e.target.value)}
                >
                  <MenuItem value="office">Office</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="field">Field</MenuItem>
                  <MenuItem value="client_site">Client Site</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={editData.employee_type}
                  onChange={(e) => handleInputChange('employee_type', e.target.value)}
                >
                  <MenuItem value="permanent">Permanent</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="temporary">Temporary</MenuItem>
                  <MenuItem value="apprentice">Apprentice</MenuItem>
                  <MenuItem value="intern">Intern</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formatDateForInput(editData.start_date)}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Salary & Benefits Tab */}
        <TabPanel value={selectedTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Annual Salary ($)"
                type="number"
                value={editData.annual_salary || ''}
                onChange={(e) => handleInputChange('annual_salary', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hourly Rate ($)"
                type="number"
                value={editData.hourly_rate || ''}
                onChange={(e) => handleInputChange('hourly_rate', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payroll Frequency</InputLabel>
                <Select
                  value={editData.payroll_frequency || 'monthly'}
                  onChange={(e) => handleInputChange('payroll_frequency', e.target.value)}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="fortnightly">Fortnightly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.pension_scheme === 'enrolled' || false}
                    onChange={(e) => handleInputChange('pension_scheme', e.target.checked ? 'enrolled' : 'not_enrolled')}
                  />
                }
                label="Auto-enroll in Pension Scheme"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.student_loan || false}
                    onChange={(e) => handleInputChange('student_loan', e.target.checked)}
                  />
                }
                label="Student Loan Deductions"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tax & Banking Tab */}
        <TabPanel value={selectedTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Social Security Number"
                value={editData.social_security_number || ''}
                onChange={(e) => handleInputChange('social_security_number', e.target.value.toUpperCase())}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tax Code</InputLabel>
                <Select
                  value={editData.tax_code || '1257L'}
                  onChange={(e) => handleInputChange('tax_code', e.target.value)}
                >
                  <MenuItem value="1257L">1257L - Standard personal allowance</MenuItem>
                  <MenuItem value="BR">BR - Basic rate on all income</MenuItem>
                  <MenuItem value="D0">D0 - Higher rate on all income</MenuItem>
                  <MenuItem value="D1">D1 - Additional rate on all income</MenuItem>
                  <MenuItem value="NT">NT - No tax</MenuItem>
                  <MenuItem value="0T">0T - No personal allowance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>Bank Details</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Name"
                value={editData.bank_name || ''}
                onChange={(e) => handleInputChange('bank_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={editData.account_holder_name || ''}
                onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Routing Number"
                value={editData.routing_number || ''}
                onChange={(e) => handleInputChange('routing_number', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={editData.account_number || ''}
                onChange={(e) => handleInputChange('account_number', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Emergency Contact Tab */}
        <TabPanel value={selectedTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={editData.emergency_contact_name || ''}
                onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={editData.emergency_contact_phone || ''}
                onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={editData.emergency_contact_relationship || ''}
                  onChange={(e) => handleInputChange('emergency_contact_relationship', e.target.value)}
                >
                  <MenuItem value="spouse">Spouse</MenuItem>
                  <MenuItem value="partner">Partner</MenuItem>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="sibling">Sibling</MenuItem>
                  <MenuItem value="child">Child</MenuItem>
                  <MenuItem value="friend">Friend</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEmployeeDialog;
